import { Button, TextField } from "@flexfreight/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ApiError } from "../api/client";
import { SESSION_KEY } from "./ConsoleGate";
import { passwordApi } from "./session";
import { AuthFrame } from "./SignIn";

/** Same rules as the customer portal and the API (NIST-style length only; the API caps at 256). */
export const PASSWORD_MIN = 12;
export function checkPassword(v: string): string | null {
  if (v.length < PASSWORD_MIN) return `Password must be at least ${PASSWORD_MIN} characters`;
  if (v.length > 256) return "Password is too long";
  return null;
}

/**
 * /set-password?token=… (alias /reset-password): where a staff invite or a staff password-reset link lands. Shows
 * whose account the link is for, takes a new password, then sends them to the console sign-in. Customer links never
 * come here (they open the customer portal).
 */
export function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = params.get("token") ?? "";
  const tokenOk = token.length >= 20 && token.length <= 128;
  const who = useQuery({ queryKey: ["auth", "reset-lookup", token], queryFn: () => passwordApi.lookup(token), enabled: tokenOk, retry: false });
  const invite = who.data?.invite ?? params.get("invite") === "1";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const bad = checkPassword(password) ?? (confirm === password ? null : "Passwords don't match");
    setFieldError(bad);
    setError(null);
    if (bad) return;
    setBusy(true);
    try {
      await passwordApi.reset(token, password);
      // Every session for that account was revoked server side; the gate must look again.
      void qc.invalidateQueries({ queryKey: SESSION_KEY });
      void navigate(`/?${invite ? "invited" : "reset"}=1`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 400 ? "This link is invalid or has expired. Ask a FlexFreight owner or admin for a new one." : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const expired = !tokenOk || who.isError;
  if (expired) {
    return (
      <AuthFrame title={invite ? "Invitation link not valid" : "Reset link not valid"} lead={tokenOk ? "This link has expired or was already used. Ask a FlexFreight owner or admin for a new one." : "This link is incomplete. Open it again from your email."}>
        <div className="ffa-auth__form">
          <Link className="ffx-btn ffx-btn--primary" to="/">
            Go to sign in
          </Link>
        </div>
      </AuthFrame>
    );
  }
  const w = who.data;
  return (
    <AuthFrame
      title={invite ? "Choose a password" : "Choose a new password"}
      lead={
        w ? (
          <>
            {invite ? "You're invited to the FlexFreight admin console. " : ""}This link is for <strong>{w.email}</strong>
            {w.name ? ` (${w.name})` : ""}. {invite ? "Set a password to finish your staff account." : "Saving a new password signs this account out everywhere."}
          </>
        ) : (
          "Checking your link…"
        )
      }
    >
      <form className="ffa-auth__form" onSubmit={submit} noValidate aria-busy={who.isPending}>
        {w ? <input type="email" name="username" autoComplete="username" value={w.email} readOnly hidden /> : null}
        <TextField label={invite ? "Password" : "New password"} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} hint={`At least ${PASSWORD_MIN} characters.`} error={fieldError && fieldError !== "Passwords don't match" ? fieldError : undefined} required disabled={!w} />
        <TextField label="Confirm password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} error={fieldError === "Passwords don't match" ? fieldError : undefined} required disabled={!w} />
        {error ? (
          <p className="ffa-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" loading={busy} disabled={!w || !password || !confirm}>
          {invite ? "Set password and continue" : "Save new password"}
        </Button>
        <Link className="ffx-btn ffx-btn--ghost" to="/">
          Back to sign in
        </Link>
      </form>
    </AuthFrame>
  );
}
